import React, { useEffect, useState } from "react";

export default function HomePage() {
  const [active, setActive] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "about", "projects", "contact"];
      let currentSection = "home";

      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (
            rect.top <= window.innerHeight * 0.4 &&
            rect.bottom >= window.innerHeight * 0.4
          ) {
            currentSection = sectionId;
            break;
          }
        }
      }

      setActive(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initial call
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="h-screen w-screen overflow-y-scroll scroll-smooth snap-y snap-mandatory font-sans">
      {/* --- Section: HOME --- */}
      <section
        id="home"
        className="snap-start h-screen w-full bg-[#F5F3EB] flex items-center justify-center"
      >
        <div className="flex justify-center items-center w-full h-full">
          <img
            src="/turtley-logo.jpeg"
            alt="Turtley Techy Logo"
            className="h-[80vh] sm:h-[85vh] max-h-full w-auto object-contain"
          />
        </div>
      </section>

      {/* --- Section: ABOUT --- */}
      <section
        id="about"
        className="snap-start h-screen w-full bg-[#0C2E2E] text-[#E1F0DF] flex flex-col justify-center items-center px-4 sm:px-6 py-6 overflow-y-auto"
      >
        <div className="text-center max-w-[900px] w-full px-2 font-heading text-base sm:text-2xl leading-relaxed space-y-6 sm:space-y-12">
          <h2 className="text-sm sm:text-xl font-extrabold tracking-widest text-[#9dbfaa] uppercase">
            WHO WE ARE
          </h2>

          <p className="font-semibold text-[1.1rem] sm:text-[2rem] leading-snug max-w-[750px] mx-auto">
            Turtley Techy is a two-person tech studio where we prototype our own
            tools, apps, and mildly questionable inventions.
          </p>

          <p className="font-normal text-[1.05rem] sm:text-[1.8rem] leading-snug max-w-[750px] mx-auto">
            We’re not chasing scale, we’re chasing sparks by building ideas just
            to see where they go.
          </p>

          <p className="font-semibold text-[1.1rem] sm:text-[2rem] leading-snug max-w-[750px] mx-auto">
            And if something we make ends up helping someone, even better.
          </p>

          <h2 className="text-sm sm:text-xl font-extrabold tracking-widest text-[#9dbfaa] uppercase">
            OUR MISSION
          </h2>
        </div>
      </section>

      {/* --- Section: PROJECTS --- */}
      <section
        id="projects"
        className="snap-start h-screen w-full bg-[#F5F3EB] text-[#0C2E2E] flex flex-col justify-start sm:justify-center items-center px-4 sm:px-6 py-6 sm:py-6 pt-10 overflow-y-auto"
      >
        <div className="max-w-[1100px] w-full">
          <h2 className="text-sm sm:text-xl font-extrabold tracking-widest text-[#0C2E2E]/60 uppercase text-center mb-3">
            OUR PROJECTS
          </h2>
          <p className="text-center text-[#0C2E2E]/70 text-sm sm:text-base max-w-[500px] mx-auto mb-10 sm:mb-14">
            A few sparks we've built and shipped, live and ready to poke at.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                name: "Airsoft Booking",
                icon: "🎯",
                tag: "Booking",
                description:
                  "Booking platform for airsoft fields and events.",
                url: "https://airsoft-booking.vercel.app/",
                image: "/projects/airsoft-booking.jpg",
              },
              {
                name: "BuildWise",
                icon: "🧱",
                tag: "Planning",
                description: "Smart tool for planning and tracking builds.",
                url: "https://build-wise-green.vercel.app/",
                image: "/projects/build-wise.jpg",
              },
              {
                name: "Fly Calm",
                icon: "✈️",
                tag: "Wellness",
                description:
                  "App to help ease anxiety before and during flights.",
                url: "https://fly-calm.vercel.app/",
                image: "/projects/fly-calm.jpg",
              },
            ].map((project) => (
              <a
                key={project.name}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col bg-[#0C2E2E] text-[#E1F0DF] rounded-2xl shadow-md ring-1 ring-[#9dbfaa]/20 hover:ring-[#9dbfaa]/70 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#0C2E2E]/30 transition-all duration-300 overflow-hidden"
              >
                {/* --- Browser preview mockup --- */}
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#08201f]">
                  <div className="absolute top-0 left-0 right-0 h-6 bg-[#08201f] flex items-center gap-1.5 px-3 z-10">
                    <span className="h-2 w-2 rounded-full bg-[#e1f0df]/20" />
                    <span className="h-2 w-2 rounded-full bg-[#e1f0df]/20" />
                    <span className="h-2 w-2 rounded-full bg-[#e1f0df]/20" />
                  </div>
                  <img
                    src={project.image}
                    alt={`${project.name} preview`}
                    loading="lazy"
                    className="absolute top-6 left-0 w-full h-[calc(100%-1.5rem)] object-cover object-top scale-100 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C2E2E] via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-300" />
                </div>

                {/* --- Content --- */}
                <div className="relative flex flex-col flex-1 p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl sm:text-3xl">{project.icon}</span>
                    <span className="text-[10px] sm:text-xs font-semibold tracking-wider uppercase text-[#9dbfaa] bg-[#9dbfaa]/10 rounded-full px-3 py-1">
                      {project.tag}
                    </span>
                  </div>

                  <h3 className="font-heading font-semibold text-lg sm:text-xl mb-2">
                    {project.name}
                  </h3>
                  <p className="text-sm sm:text-base text-[#9dbfaa] leading-relaxed mb-5">
                    {project.description}
                  </p>

                  <span className="mt-auto inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#0C2E2E] bg-[#9dbfaa] rounded-lg py-2.5 group-hover:bg-[#e1f0df] transition-colors duration-300">
                    Visit site
                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* --- Section: CONTACT --- */}
      <section
        id="contact"
        className="snap-start h-screen w-full bg-[#06201c] text-[#e1f0df] flex flex-col justify-center items-center px-6"
      >
        <h2 className="text-sm font-bold tracking-widest text-[#9dbfaa] mb-8">
          CONTACT US
        </h2>
        <form
          action="https://formspree.io/f/xvgqadpl"
          method="POST"
          className="w-full max-w-md space-y-6 text-left"
        >
          <div>
            <label htmlFor="name" className="block text-sm mb-1">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="w-full p-3 rounded bg-[#e1f0df] text-[#0C2E2E] focus:outline-none"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full p-3 rounded bg-[#e1f0df] text-[#0C2E2E] focus:outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm mb-1">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              rows="4"
              required
              className="w-full p-3 rounded bg-[#e1f0df] text-[#0C2E2E] focus:outline-none"
              placeholder="Write your message here..."
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#9dbfaa] text-[#0C2E2E] font-semibold py-2 rounded hover:bg-[#7ea58f] transition"
          >
            Send
          </button>
        </form>
      </section>

      {/* --- Dot Menu (mobile + desktop) --- */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 transform flex flex-col space-y-4 z-50 sm:right-6">
        {["home", "about", "projects", "contact"].map((section) => (
          <a
            key={section}
            href={`#${section}`}
            className={`w-3 h-3 rounded-full border-2 border-[#9dbfaa] transition ${
              active === section ? "bg-[#9dbfaa]" : ""
            }`}
            aria-label={`Go to ${section}`}
          />
        ))}
      </div>
    </div>
  );
}
